import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Img } from "@/components/site/Motion";
import { Lightbox } from "@/components/site/Lightbox";

const Slide = ({ it, i, onOpen, testid, light }) => (
  <li className="proj-slide snap-start" data-testid={`${testid}-slide-${i}`}>
    <button type="button" onClick={() => onOpen(i)} className="group block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white" aria-label={`Open gallery at: ${it.system}, ${it.setting}`}>
      <span className="img-frame block aspect-[4/3]">
        <Img name={it.name} alt={it.alt} w={it.w} h={it.h} sizes="(min-width: 768px) 40vw, 76vw" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
        <span className="absolute right-3 top-3 z-[1] inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#071422]/55 text-white backdrop-blur-md" aria-hidden="true"><Expand size={16} /></span>
      </span>
      <span className={`mt-3 block text-[15px] font-semibold ${light ? "text-[#102A43]" : "ext-head text-white"}`}>{it.system}</span>
      <span className={`block text-sm ${light ? "text-[#3E5468]" : "ext-head text-[#D6E0E9]"}`}>{it.setting}</span>
    </button>
  </li>
);

// Horizontal project slider: about 1⅓ slides on phones, 2⅓ on wide screens; the partial slide fades out to the right.
export const ProjectSlider = ({ items, filters, label = "Commercial projects", tone = "dark", testid = "project-slider", className = "mt-8 lg:mt-10" }) => {
  const light = tone === "light";
  const navCls = `btn-glass btn-icon ${light ? "" : "btn-on-dark"}`;
  const track = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });
  const [index, setIndex] = useState(null);
  const sync = useCallback(() => {
    const el = track.current;
    if (!el) return;
    setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  }, []);
  useEffect(() => {
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [sync]);
  const go = (dir) => {
    const el = track.current;
    const s = el?.querySelector(".proj-slide");
    if (!el || !s) return;
    el.scrollBy({ left: dir * (s.getBoundingClientRect().width + 16), behavior: "smooth" });
  };
  return (
    <div className={`min-w-0 ${className}`} data-testid={testid}>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className={light ? "whitespace-nowrap text-xs font-semibold uppercase tracking-[0.16em] text-[#3E5468] md:text-sm" : "eyebrow ext-head"}>{label}</p>
        <div className="flex gap-2">
          <button type="button" className={navCls} onClick={() => go(-1)} disabled={edge.start} aria-label="Previous projects" data-testid={`${testid}-prev-btn`}><ChevronLeft aria-hidden="true" size={20} /></button>
          <button type="button" className={navCls} onClick={() => go(1)} disabled={edge.end} aria-label="More projects" data-testid={`${testid}-next-btn`}><ChevronRight aria-hidden="true" size={20} /></button>
        </div>
      </div>
      <div className={`proj-fade ${edge.end ? "is-end" : ""}`}>
        <ul ref={track} onScroll={sync} className="proj-track flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2" aria-label={label} data-testid={`${testid}-track`}>
          {items.map((it, i) => <Slide key={it.name} it={it} i={i} onOpen={setIndex} testid={testid} light={light} />)}
        </ul>
      </div>
      <Lightbox items={items} filters={filters} start={index} onClose={() => setIndex(null)} testid={`${testid}-lightbox`} />
    </div>
  );
};
